import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Gallery.css';
import Lightbox from './Lightbox';
import { EASE, Reveal, SplitText } from './motion';

// Načte všechny fotky Vyrobek1..N automaticky. Ke každé fotce Vyrobek{N}.webp patří
// zmenšený náhled Vyrobek{N}-thumb.webp do mřížky, plná velikost se načte až v prohlížeči.
const full = require.context('./images', false, /^\.\/Vyrobek\d+\.webp$/);
const thumbs = require.context('./images', false, /^\.\/Vyrobek\d+-thumb\.webp$/);
const SOURCES = full
  .keys()
  .sort((a, b) => parseInt(a.match(/\d+/)[0], 10) - parseInt(b.match(/\d+/)[0], 10))
  .map((key) => ({ src: full(key), thumb: thumbs(key.replace('.webp', '-thumb.webp')) }));

const INITIAL = 8;

const Gallery = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  const images = useMemo(
    () => SOURCES.map((s, i) => ({ ...s, alt: t('gallery.alt', { n: i + 1 }) })),
    [t]
  );
  const visible = expanded ? images : images.slice(0, INITIAL);

  return (
    <section id="gallery" className="section section--light gallery">
      <div className="container">
        <div className="section-head section-head--split">
          <div className="section-head">
            <SplitText text={t('gallery.title')} className="section-head__title" />
          </div>
          <Reveal as="p" className="section-head__desc" delay={0.15}>
            {t('gallery.desc')}
          </Reveal>
        </div>

        <ul className="gallery__grid">
          {visible.map((img, i) => (
            <motion.li
              key={img.src}
              className={`gallery__item gallery__item--${i % 4}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, delay: (i % 4) * 0.08, ease: EASE }}
            >
              <button type="button" onClick={() => setOpenIndex(i)} aria-label={t('gallery.open', { n: i + 1 })}>
                <img src={img.thumb} alt={img.alt} loading="lazy" />
                <span className="gallery__zoom" aria-hidden="true">
                  +
                </span>
              </button>
            </motion.li>
          ))}
        </ul>

        {images.length > INITIAL && (
          <div className="gallery__more">
            <button type="button" className="btn btn--dark" onClick={() => setExpanded((e) => !e)}>
              {expanded ? t('gallery.less') : t('gallery.more')}
              <span className="gallery__more-count">{expanded ? '−' : `+${images.length - INITIAL}`}</span>
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {openIndex !== null && (
          <Lightbox images={images} index={openIndex} onClose={() => setOpenIndex(null)} onChange={setOpenIndex} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
